<template>
  <label :class="className">
    <input :id ref="input" v-model="model" :disabled="isDisabled" type="radio" :value="label" />

    <VCard v-if="group?.props?.type === 'card'" :clickable="!isDisabled" :ribbon-type>
      <span v-if="!componentInSlot" class="v-radio__input" />
      <label class="v-radio__label" :for="id">
        <div class="title-20">
          <slot name="title" />
          <VIconHelp :help style="margin-left: 5px" type="placeholder" />
        </div>
        <slot>{{ label }}</slot>
      </label>

      <template v-if="$slots.ribbon" #ribbon>
        <slot name="ribbon" />
      </template>
    </VCard>

    <template v-else>
      <span v-if="!componentInSlot" class="v-radio__input" />
      <label class="v-radio__label" :for="id">
        <slot>{{ label }}</slot>
        <VIconHelp :help style="margin-left: 5px" type="placeholder" />
      </label>
    </template>
  </label>
</template>

<script setup lang="ts">
import { computed, inject, useId, useSlots, useTemplateRef } from 'vue'

import { useClassName } from '../composables/useClassName.ts'
import type { VCardRibbonType } from '../types.ts'
import { $formKey, $radioGroupKey } from '../utils/injections.ts'
import VCard from './VCard.vue'
import VIconHelp from './VIconHelp.vue'

const slots = useSlots()

const group = inject($radioGroupKey, undefined)
const form = inject($formKey, undefined)

interface Props {
  modelValue?: any
  label: any
  disabled?: boolean
  ribbonType?: VCardRibbonType
  help?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  ribbonType: undefined,
  help: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: any]
}>()

defineSlots<{
  default?(): any
  title?(): any
  ribbon?(): any
}>()

const id = useId()
const input = useTemplateRef('input')

const componentInSlot = computed(() => group?.props?.type !== 'card' && slots.default && typeof slots.default()[0].type === 'object')
const isDisabled = computed(() => props.disabled || group?.props?.disabled || form?.props?.disabled)

const className = useClassName(
  'v-radio',
  computed(() => [String(props.label)]),
  computed(() => ({ 'has-radio-input': !componentInSlot.value, 'v-radio--checked': model.value === props.label, 'v-radio--disabled': isDisabled.value })),
)

const model = computed({
  get: () => group?.props?.modelValue ?? props.modelValue,
  set: (value) => {
    if (group) {
      group.input(value)
    } else {
      emit('update:modelValue', value)
    }

    if (input.value) {
      input.value.checked = model.value === props.label
    }
  },
})
</script>

<style>
.v-radio {
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  user-select: none;

  &:not(:last-child) {
    margin-right: 20px;
  }

  &:hover .v-radio__input {
    opacity: 0.5;
  }

  &.has-radio-input {
    padding-left: 20px;
  }

  /* Hide the browser's default checkbox */
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  .v-radio__input {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    left: 0;
    height: 14px;
    width: 14px;
    border-radius: 14px;
    background-color: #fff;
    border: 1px solid #000;
    transition:
      background-color,
      border-color,
      opacity var(--transition);

    &:after {
      content: '';
      display: none;
      width: 8px;
      height: 8px;
      border-radius: 8px;
      background-color: #000;
      transition: background-color var(--transition);
    }
  }

  .v-card {
    margin-bottom: 10px;
    border-radius: var(--border-radius);

    .v-radio__input {
      left: 20px;
      border-color: var(--color-grey-20);
    }

    .v-card__body {
      padding: 15px 20px;
    }

    .v-card__body-content {
      display: grid;
      align-items: center;
    }

    .v-radio__label {
      margin-left: 30px;
      display: flex;
      align-items: initial;
      flex-direction: column;
    }
  }

  &.v-radio--checked .v-card {
    border-color: var(--color-grey-80);
  }

  &.v-radio--checked:not(.v-radio--disabled) .v-card .v-radio__input {
    border-color: #000;
  }

  &.v-radio--disabled .v-card .v-radio__input {
    border-color: var(--color-text-secondary);
  }
}

.v-radio--checked .v-radio__input:after {
  display: block;
}

.v-radio__label {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.v-radio--disabled {
  color: var(--color-text-secondary);
  cursor: default;

  .v-radio__input {
    border-color: var(--color-text-secondary);

    &:after {
      background-color: var(--color-text-secondary);
    }
  }

  .v-radio__label {
    cursor: default;
  }
}

.v-radio-group--vertical .v-radio {
  display: grid;
}

.v-radio-group--card .v-radio {
  display: block;
  padding-left: 0;

  &:not(:last-child) {
    margin-right: 0;
  }
}

.v-radio-group--tabs .v-radio:not(.v-radio--disabled):hover {
  background-color: #fff;
}

.v-radio-group--tabs .v-radio,
.v-radio-group--tabs-bordered .v-radio,
.v-radio-group--tabs-bordered-primary .v-radio {
  padding: 5px 10px;
  border-radius: var(--border-radius);
  transition:
    background-color var(--transition),
    box-shadow var(--transition);

  &:not(:last-child) {
    margin-right: 13px;
  }

  &:not(.v-radio--disabled):hover {
    box-shadow: 0 0 6px 0 rgb(0, 0, 0, 0.1);
  }

  .v-radio__input {
    display: none;
  }
}

.v-radio-group--tabs .v-radio--checked {
  background-color: #fff;
}

.v-radio-group--tabs-bordered .v-radio {
  border: 1px solid var(--color-grey-10);

  &.v-radio--checked {
    background-color: var(--color-grey-10);
  }
}

.v-radio-group--tabs-bordered-primary .v-radio {
  border: 1px solid var(--color-grey-10);

  &.v-radio--checked {
    border: 1px solid var(--color-primary);
    color: var(--color-primary);
  }
}

.v-radio-group--toggle .v-radio {
  padding: 5px 10px;
  border: solid var(--color-grey-10);
  transition:
    background-color var(--transition),
    box-shadow var(--transition);
  border-width: 1px;

  &:first-child {
    border-width: 1px 0px 1px 1px;
    border-radius: var(--border-radius) 0px 0px var(--border-radius);
  }

  &:last-child {
    border-width: 1px 1px 1px 0px;
    border-radius: 0px var(--border-radius) var(--border-radius) 0px;
  }

  &:not(:last-child) {
    margin-right: 0px;
  }

  &.v-radio--checked {
    background-color: var(--color-grey-10);
  }

  &:not(.v-radio--disabled):hover {
    box-shadow: 0 0 6px 0 rgb(0, 0, 0, 0.1);
  }

  .v-radio__input {
    display: none;
  }
}
</style>
